import {
    UnauthorizedException,
    type Content,
    type DiscoveryContent,
    type DiscoverySearchResponse,
} from "@amzn/ads-discovery-theme-service-typescript-client";
import { type AsyncStatus, useDebouncedEffect } from "@react-hookz/web";
import { isEmpty } from "lodash";
import { useCallback, useState } from "react";
import { useStore } from "zustand";

import { MARKETING_NARRATIVES_CATEGORY_DELIMINITER } from "../../constants/filters";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { discoveryFilterStore } from "../../store/filters";
import type { Item } from "../../types/common";
import { MarketingNarrativesFilterField } from "../../types/filters/marketingNarratives";
import type { MarketingContentType } from "../../types/providers";
import { IHubContentProvider } from "../../types/providers";
import { isLoading } from "../../utils/api.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import { useAsyncErrorStatusNotification } from "../notifications/useAsyncErrorStatusNotification";

import { useSearchMarketingNarrativesAction } from "./useMarketingNarrativesAction";
import { useSingleMarketingNarrativeAction } from "./useSingleMarketingContentLoader";

export interface MarketingNarrativesLoaderProps {
    isLoaderInView?: boolean;
    marketingContentId?: string;
}

export const FILE_TYPES_TO_INCLUDE = [
    ".pptx",
    ".docx",
    ".xlsx",
    ".pdf",
    ".jpg",
    ".png",
    ".mp4",
];

const INITIAL_SEARCH_RESPONSE = {
    contents: [],
    nextPageToken: "",
    numHits: 0,
};

const getCategoryIds = () => {
    const marketingNarrativeFilters =
        discoveryFilterStore.getState().MARKETING_CONTENT.filters[
            IHubContentProvider.MARKETING_NARRATIVES
        ];
    const contentSubType =
        discoveryFilterStore.getState().MARKETING_CONTENT.contentSubType;
    const fieldSelections = marketingNarrativeFilters.fieldSelections;
    return Object.entries(fieldSelections)
        .filter(
            ([key]) =>
                key !== MarketingNarrativesFilterField.FILE_TYPE.toString(),
        )
        .flatMap(([, selection]) => selection as Item[])
        .map((item) => item.value)
        .concat([contentSubType.scope as MarketingContentType])
        .join(MARKETING_NARRATIVES_CATEGORY_DELIMINITER);
};

const getSearchQuery = () => {
    return discoveryFilterStore.getState().MARKETING_CONTENT.filters[
        IHubContentProvider.MARKETING_NARRATIVES
    ].searchQuery;
};

const getFileTypeFilters = () => {
    const marketingNarrativeFilters =
        discoveryFilterStore.getState().MARKETING_CONTENT.filters[
            IHubContentProvider.MARKETING_NARRATIVES
        ];
    const fieldSelections = marketingNarrativeFilters.fieldSelections;

    if (!isEmpty(fieldSelections[MarketingNarrativesFilterField.FILE_TYPE])) {
        return fieldSelections[MarketingNarrativesFilterField.FILE_TYPE]
            .map((item) => item.value)
            .join(MARKETING_NARRATIVES_CATEGORY_DELIMINITER);
    } else {
        return FILE_TYPES_TO_INCLUDE.join(
            MARKETING_NARRATIVES_CATEGORY_DELIMINITER,
        );
    }
};

export function useMarketingNarrativesLoader({
    isLoaderInView,
    marketingContentId,
}: MarketingNarrativesLoaderProps): [AsyncStatus, Content[], number, boolean] {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const [searchResponse, setSearchResponse] =
        useState<DiscoverySearchResponse>(INITIAL_SEARCH_RESPONSE);
    const [lazyLoadedContents, setLazyLoadedContents] = useState<
        DiscoveryContent[]
    >([] as DiscoveryContent[]);
    const [setData, sortAttribute] = useStore(discoveryFilterStore, (state) => [
        state.setData,
        state.MARKETING_CONTENT.filters[
            IHubContentProvider.MARKETING_NARRATIVES
        ].sortAttribute,
    ]);
    const searchQuery = getSearchQuery();
    const categoryIds = getCategoryIds();
    const fileTypeFilters = getFileTypeFilters();

    const [searchMarketingContentsState, searchMarketingContentsAction] =
        useSearchMarketingNarrativesAction();

    const [singleMarketingContentState, singleMarketingContentAction] =
        useSingleMarketingNarrativeAction();

    const [isUnauthorized, setIsUnauthorized] = useState(false);

    const fetchSingleMarketingContent = useCallback(
        async (contentId: string) => {
            if (isLoading(singleMarketingContentState.status))
                return Promise.resolve();
            const currentContent =
                await singleMarketingContentAction.execute(contentId);
            setLazyLoadedContents([currentContent]);
            setSearchResponse({
                ...INITIAL_SEARCH_RESPONSE,
                numHits: 1,
            });
        },
        [singleMarketingContentAction, singleMarketingContentState],
    );

    const fetchMarketingNarratives = useCallback(
        async (reset = false) => {
            try {
                if (marketingContentId) {
                    await fetchSingleMarketingContent(marketingContentId);
                    return;
                }
                if (isLoading(searchMarketingContentsState.status))
                    return Promise.resolve();

                const filters = [`format:${fileTypeFilters}`];
                if (categoryIds.length > 0) {
                    filters.push(`category:${categoryIds}`);
                }

                if (reset) {
                    setData(IHubContentProvider.MARKETING_NARRATIVES, []);
                    setSearchResponse(INITIAL_SEARCH_RESPONSE);
                    setLazyLoadedContents([]);
                }

                const currentResponse =
                    await searchMarketingContentsAction.execute(
                        filters,
                        searchQuery,
                        reset ? "" : searchResponse.nextPageToken,
                        sortAttribute,
                    );

                logInsightBrowseResults(
                    currentResponse.numHits,
                    useUnifiedSearch,
                );

                setData(
                    IHubContentProvider.MARKETING_NARRATIVES,
                    currentResponse.contents,
                );
                setSearchResponse(currentResponse);
                setLazyLoadedContents((prevContents) =>
                    reset
                        ? (currentResponse.contents ?? [])
                        : [
                              ...prevContents,
                              ...(currentResponse.contents ?? []),
                          ],
                );
            } catch (error) {
                if (error instanceof UnauthorizedException) {
                    setIsUnauthorized(true);
                }
                throw error;
            }
        },
        [
            searchMarketingContentsAction,
            setData,
            setSearchResponse,
            setLazyLoadedContents,
            searchMarketingContentsState,
            searchResponse,
            categoryIds,
            searchQuery,
            fileTypeFilters,
            sortAttribute,
        ],
    );

    /**
     * Triggers for below changes
     * 1. When any of filter option get changed including FileType filter
     * 2. When search query is changed,
     * 3. When sortAttribute is changed
     */
    useDebouncedEffect(
        () => {
            void fetchMarketingNarratives(true);
        },
        [categoryIds, searchQuery, fileTypeFilters, sortAttribute],
        500,
    );

    /**
     * Triggers during vertical scroll when user reaches end of the current results
     */
    useDebouncedEffect(
        () => {
            if (
                isLoaderInView &&
                (searchResponse.numHits ?? 0) > lazyLoadedContents.length
            ) {
                void fetchMarketingNarratives();
            }
        },
        [isLoaderInView, searchResponse, lazyLoadedContents],
        500,
    );

    useAsyncErrorStatusNotification(searchMarketingContentsState);

    return [
        marketingContentId
            ? singleMarketingContentState.status
            : searchMarketingContentsState.status,
        lazyLoadedContents,
        searchResponse.numHits ?? 0,
        isUnauthorized,
    ];
}
